import {
  Body1Strong,
  Caption1,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import type { ReactNode } from "react";
import { baeTypography } from "../theme/baeTheme";
import { elevation, motion, PHONE } from "./tokens";

const useStyles = makeStyles({
  card: {
    display: "grid",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    // A hairline plus a soft shadow, instead of the 1px box a card used to be. The border keeps
    // the edge crisp on a dark ground, where a shadow carries no separation at all.
    boxShadow: elevation.restLight,
    minWidth: 0,
    [PHONE]: { padding: tokens.spacingHorizontalM },
  },
  interactive: {
    transitionProperty: "box-shadow, transform",
    transitionDuration: motion.base,
    ":hover": {
      boxShadow: elevation.hoverLight,
      transform: "translateY(-1px)",
    },
  },
  quiet: {
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: "none",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  flush: { padding: 0, overflow: "hidden" },

  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  headerText: { display: "grid", gap: "1px", minWidth: 0, flexGrow: 1 },
  // The icon is a landmark, not decoration: it is what lets someone find "Where it is" again
  // after scrolling, without reading.
  icon: {
    display: "grid",
    placeItems: "center",
    width: "1.75rem",
    height: "1.75rem",
    flexShrink: 0,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorCompoundBrandForeground1,
    fontSize: "16px",
  },
  title: { fontFamily: baeTypography.fontFamilyDisplay, letterSpacing: "-0.01em" },
  muted: { color: tokens.colorNeutralForeground3 },
  actions: { display: "flex", gap: tokens.spacingHorizontalXS, flexWrap: "wrap" },
});

/**
 * The one card in the console.
 *
 * Every panel used to repeat the same eight lines of `makeStyles`, which meant every panel was
 * free to drift a pixel from the others — and eighteen panels that nearly agree read as unfinished
 * in a way one that disagrees never would.
 */
export function Card({
  children,
  className,
  interactive = false,
  quiet = false,
  flush = false,
  style,
}: {
  children: ReactNode;
  className?: string;
  /** Lifts on hover. For a card that opens something. */
  interactive?: boolean;
  /** Recedes instead of standing out. For a nested panel inside another card. */
  quiet?: boolean;
  /** Drops the padding, for content that runs to the edge — a photo, a table. */
  flush?: boolean;
  style?: React.CSSProperties;
}) {
  const styles = useStyles();

  return (
    <div
      className={mergeClasses(
        styles.card,
        interactive && styles.interactive,
        quiet && styles.quiet,
        flush && styles.flush,
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}

/** A card's own heading: an icon to find it by, a title, a line saying why it is there. */
export function CardHeader({
  icon,
  title,
  hint,
  actions,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  actions?: ReactNode;
}) {
  const styles = useStyles();

  return (
    <div className={styles.header}>
      {icon && <span className={styles.icon}>{icon}</span>}
      <span className={styles.headerText}>
        <Body1Strong className={styles.title}>{title}</Body1Strong>
        {hint && <Caption1 className={styles.muted}>{hint}</Caption1>}
      </span>
      {actions && <span className={styles.actions}>{actions}</span>}
    </div>
  );
}
